import { useMemo } from 'react';
import type { LabReport } from '@/types/lab-results-api';
import {
  isResultsNotDeployed,
  useLabRequestReports,
  usePatientExternalLabReports,
  usePatientLabRequests,
} from '@/patients/services/use-patient-lab-results';
import {
  buildExternalLabResultGroup,
  buildLabResultGroup,
  currentExternalReports,
  labsByRootReport,
  uniqueLabRequests,
  type LabResultGroup,
} from '@/patients/lib/lab-results';

export interface LabResultGroupsState {
  /**
   * True only once a Reports read has answered `storeAvailable: true`. Until then (the tables are
   * not there yet, #997 is not deployed, or the reads failed) the Labs tab renders exactly as it
   * did before PROD-2643.
   */
  available: boolean;
  groups: LabResultGroup[];
  /** Outside (External) reports with no lab request, one group each (PROD-3275). */
  externalGroups: LabResultGroup[];
  /** Root report id to performing lab, over every report loaded: what a trend point's lab is read from. */
  labsByRoot: ReadonlyMap<string, string>;
  /**
   * Lab requests whose Reports read failed for a reason other than "not deployed" (404), after
   * a retry. Shown as a failure line, never silently dropped.
   */
  failed: { labRequestId: string; name: string }[];
}

/** Lab requests of this patient, each joined to its Reports read (PROD-2643). */
export function useLabResultGroups(patientId: string | null | undefined): LabResultGroupsState {
  const requestsQuery = usePatientLabRequests(patientId);
  const requests = useMemo(
    () => uniqueLabRequests(requestsQuery.data?.patientLabOrderDetails),
    [requestsQuery.data],
  );
  const reportQueries = useLabRequestReports(
    patientId,
    requests.map((r) => r.id as string),
    true, // includeHistory: an amended analyte shows its earlier versions (PROD-3275)
  );
  const externalQuery = usePatientExternalLabReports(patientId);

  const dataKey =
    reportQueries
      .map((q) => `${q.dataUpdatedAt}:${q.isError && !isResultsNotDeployed(q.error) ? 'e' : ''}`)
      .join('|') + `#${externalQuery.dataUpdatedAt}:${externalQuery.isError ? 'e' : ''}`;
  return useMemo(() => {
    const groups: LabResultGroup[] = [];
    const allReports: LabReport[] = [];
    const failed: LabResultGroupsState['failed'] = [];
    let available = false;
    requests.forEach((request, i) => {
      const q = reportQueries[i];
      if (q?.isError && !isResultsNotDeployed(q.error)) {
        failed.push({ labRequestId: request.id as string, name: request.orderName?.trim() || 'Lab order' });
        return;
      }
      const reads = q?.data;
      if (!reads || reads.storeAvailable === false) return;
      available = true;
      allReports.push(...(reads.reports ?? []));
      groups.push(buildLabResultGroup(request, reads));
    });

    // Outside reports: a 404 (not deployed) or storeAvailable false is "none", like the reads above.
    const external = externalQuery.data;
    const externalGroups: LabResultGroup[] = [];
    if (externalQuery.isError && !isResultsNotDeployed(externalQuery.error)) {
      failed.push({ labRequestId: 'outside-reports', name: 'outside lab reports' });
    } else if (external && external.storeAvailable !== false) {
      const history = external.reports ?? [];
      allReports.push(...history);
      for (const report of currentExternalReports(history)) {
        externalGroups.push(buildExternalLabResultGroup(report, history));
      }
      if (externalGroups.length > 0) available = true;
    }
    return { available, groups, externalGroups, labsByRoot: labsByRootReport(allReports), failed };
    // reportQueries is a new array each render; dataKey tracks the data it carries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, dataKey]);
}
